import { useEffect, useState } from "react";
import { OwnerManualsHeader } from "./components/OwnersManualHeader";
import { ExternalIcon } from "./compatibility-widget";
import { useTranslation } from "react-i18next";

type PropsType = {
  permanentDomain: string;
};

type ManualsType = {
  id: number;
  modelId: string;
  modelName?: string;
  ownerManualId: string;
  url: string;
  year?: number;
  type?: string;
  brand?: string;
};

const ITEMS_PER_PAGE = 100;

export const OwnerManualsListWidget = ({ permanentDomain }: { permanentDomain: string }) => {
  const [manuals, setManuals] = useState<ManualsType[]>([]);
  const [ktmPartsFinderPath, setKtmPartsFinderPath] = useState<string>("");
  const [husqvarnaPartsFinderPath, setHusqvarnaPartsFinderPath] = useState<string>("");
  const [gasgasPartsFinderPath, setGasgasPartsFinderPath] = useState<string>("");

  useEffect(() => {
    fetchManuals();
  }, []);

  const fetchManuals = async () => {
    const response = await fetch(`https://partsfinder.clpstudio.ro/api/ownermanuals`, {
      method: "POST",
      body: JSON.stringify({ permanentDomain }),
    });

    const data = await response.json();
    const manuals = data?.data || [];
    const ktmPartsFinderPath = data?.ktmPartsFinderPath || "";
    const husqvarnaPartsFinderPath = data?.husqvarnaPartsFinderPath || "";
    const gasgasPartsFinderPath = data?.gasgasPartsFinderPath || "";

    console.log("Manuals", manuals);
    setManuals(manuals);
    setKtmPartsFinderPath(ktmPartsFinderPath);
    setHusqvarnaPartsFinderPath(husqvarnaPartsFinderPath);
    setGasgasPartsFinderPath(gasgasPartsFinderPath);
  };

  return (
    <main className="w-full flex flex-col items-center justify-between" style={{ backgroundColor: "white" }}>
      <div className="w-full items-start justify-between flex-col" style={{ maxWidth: 1366 }}>
        <OwnerManualsHeader />

        <ListManuals
          manuals={manuals}
          ktmPartsFinderPath={ktmPartsFinderPath}
          husqvarnaPartsFinderPath={husqvarnaPartsFinderPath}
          gasgasPartsFinderPath={gasgasPartsFinderPath}
        />
      </div>
    </main>
  );
};

const ListManuals = ({
  manuals,
  ktmPartsFinderPath,
  husqvarnaPartsFinderPath,
  gasgasPartsFinderPath,
}: {
  manuals: ManualsType[];
  ktmPartsFinderPath: string;
  husqvarnaPartsFinderPath: string;
  gasgasPartsFinderPath: string;
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  const filteredManuals = manuals.filter((manual) => {
    const search = searchTerm.toLowerCase();
    const modelName = manual.modelName?.toLowerCase() || "";
    const brand = manual.brand?.toLowerCase() || "";
    const type = manual.type?.toLowerCase() || "";
    const year = manual.year?.toString() || "";

    // Check if search term contains multiple words
    const searchTerms = search.split(/\s+/).filter((term) => term.length > 0);

    if (searchTerms.length <= 1) {
      // Single word search - check if any field contains it
      return modelName.includes(search) || brand.includes(search) || type.includes(search) || year.includes(search);
    } else {
      // Multi-word search - check if all terms are found across any fields
      return searchTerms.every((term) => modelName.includes(term) || brand.includes(term) || type.includes(term) || year.includes(term));
    }
  });

  const totalPages = Math.ceil(filteredManuals.length / ITEMS_PER_PAGE);
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentItems = filteredManuals.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className="flex flex-col w-full p-4 overflow-x-auto">
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search manuals..."
          className="w-full py-2 px-4 border border-gray-300 rounded"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      {isMobile ? (
        <div className="grid grid-cols-1 gap-4">
          {currentItems.map((manual) => (
            <div key={manual.id} className="bg-white border border-gray-300 rounded p-4 shadow-sm">
              <div className="mb-2">
                <span className="font-semibold">Brand:</span> {manual.brand?.toUpperCase() || "-"}
              </div>
              <div className="mb-2">
                <span className="font-semibold">Model:</span> {manual.modelName || "-"}
              </div>
              <div className="mb-2">
                <span className="font-semibold">Type:</span> {manual.type || "-"}
              </div>
              <div className="mb-2">
                <span className="font-semibold">Year:</span> {manual.year || "-"}
              </div>
              <div className="flex flex-col gap-2 mt-3">
                <a href={manual.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                  Download Manual <ExternalIcon />
                </a>
                <button
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-2 py-1 text-blue-600 bg-transparent hover:bg-gray-100 transition-colors border-none"
                  onClick={() => {
                    if (manual.brand && manual.year && manual.type && manual.modelId) {
                      onClickOpenPartsFinder(
                        manual.brand,
                        manual.year.toString(),
                        manual.type,
                        manual.modelId,
                        ktmPartsFinderPath,
                        husqvarnaPartsFinderPath,
                        gasgasPartsFinderPath
                      );
                    }
                  }}
                >
                  Spare Parts <ExternalIcon />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full bg-white border border-gray-300 overflow-x-auto" style={{ overflowX: "auto" }}>
            <thead>
              <tr className="bg-gray-100">
                <th className="py-2 px-4 border-b text-left">Brand</th>
                <th className="py-2 px-4 border-b text-left">Model</th>
                <th className="py-2 px-4 border-b text-left">Type</th>
                <th className="py-2 px-4 border-b text-left">Year</th>
                <th className="py-2 px-4 border-b text-left">Manual</th>
                <th className="py-2 px-4 border-b text-left">Spare Parts</th>
              </tr>
            </thead>
            <tbody className="overflow-x-auto">
              {currentItems.map((manual) => (
                <tr key={manual.id} className="">
                  <td className="py-2 px-4 border-b">{manual.brand?.toUpperCase() || "-"}</td>
                  <td className="py-2 px-4 border-b">{manual.modelName || "-"}</td>
                  <td className="py-2 px-4 border-b">{manual.type || "-"}</td>
                  <td className="py-2 px-4 border-b">{manual.year || "-"}</td>
                  <td className="py-2 px-4 border-b">
                    <a href={manual.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      Download
                    </a>
                  </td>
                  <td className="py-2 px-4 border-b flex items-center gap-2">
                    <button
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 px-2 py-1 text-blue-600 bg-transparent hover:bg-gray-100 transition-colors border-none"
                      onClick={() => {
                        if (manual.brand && manual.year && manual.type && manual.modelId) {
                          onClickOpenPartsFinder(
                            manual.brand,
                            manual.year.toString(),
                            manual.type,
                            manual.modelId,
                            ktmPartsFinderPath,
                            husqvarnaPartsFinderPath,
                            gasgasPartsFinderPath
                          );
                        }
                      }}
                    >
                      Spare Parts
                      <ExternalIcon />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center mt-4">
          <div className="flex space-x-2">
            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border rounded disabled:opacity-50">
              Previous
            </button>

            <span className="px-3 py-1">
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <div className="mt-2 text-sm text-gray-500">
        Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredManuals.length)} of {filteredManuals.length} manuals
      </div>
    </div>
  );
};

const onClickOpenPartsFinder = (
  brand: string,
  year: string,
  ridingTypeName: string,
  modelId: string,
  ktmPartsFinderPath: string,
  husqvarnaPartsFinderPath: string,
  gasgasPartsFinderPath: string
) => {
  if (!brand || !year || !ridingTypeName || !modelId) {
    return;
  }

  const path = [brand, year, ridingTypeName, modelId].map((t) => t.replaceAll(" ", "+")).join("-");

  let basePath = "";
  if (brand.toLocaleUpperCase() === "KTM") {
    basePath = ktmPartsFinderPath;
  } else if (brand.toLocaleUpperCase() === "HUSQVARNA") {
    basePath = husqvarnaPartsFinderPath;
  } else if (brand.toLocaleUpperCase() === "GASGAS") {
    basePath = gasgasPartsFinderPath;
  }

  const finalPath = `${basePath}?location=` + path;

  const newPage = window.location.protocol + "//" + window.location.hostname + finalPath;
  window.open(newPage, "_blank");
};
